import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ExternalLink, Ruler } from "lucide-react";

import type { EchoflicksFloorPlan } from "../types";
import {
  countCheckEntries,
  floorPlanLevelGroups,
  floorPlanPageCaption,
  floorPlanPageSources,
  floorPlanSourceRows,
  floorPlanStatusTone,
  floorPlanSummaryLine,
  formatAreaM2,
  formatMs,
  humanizeLevelKind,
  planCoverageView,
  propertyTypeSummary,
  roomsTableRows,
  type CountCheckEntry,
  type FloorPlanLevelGroup,
  type FloorPlanRoomRow,
  type PlanCoverageView,
  type PropertyTypeSummary,
} from "./sessionUtils";
import { useMediaUrl, type MediaUrlSource } from "./useMediaUrl";

// ---------------------------------------------------------------------------
// FloorPlanCard — the floor-plan read the bot attached to a listing ingest
// (FPR-014/016, `floor_plan.v1`): whether a plan was found and read, what it
// was (vector/raster PDF, image), the room schedule it produced (raw label →
// name → category → m² → floor), the plan-vs-listing room-count check, the
// orientation evidence, and the page renders. Sub-card of the Listing Ingest
// card; the parent renders it only when `ingest.floor_plan` exists.
//
// `floor_plan.v2` (FPV-031) adds three things, all feature-detected so a v1
// block renders byte-identically: the derived property type, the openings
// count in the summary line, and a per-level grouping of the rooms table.
//
// BLD-043 (plan sets, contract §2.1) adds, again feature-detected: plan
// coverage (status, gaps in plain words, every candidate read/dropped/unread
// with its reason), the plan sources with how each was found, the floor title
// under each page thumbnail, and kind/storey/building on the level headers.
// ---------------------------------------------------------------------------

// The amber row treatment IngestFieldTable uses for a recipe-vs-LLM mismatch.
const MISMATCH_CLASS =
  "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200";

/** Plan-vs-listing (or mapped-vs-plan-vs-listing) room counts, mismatches amber. */
export const CountCheckLine = ({
  entries,
  testId,
}: {
  entries: CountCheckEntry[];
  testId?: string;
}) => {
  if (entries.length === 0) return null;
  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      <span className="text-muted-foreground">Count check:</span>
      {entries.map((entry) => (
        <span
          key={entry.key}
          data-mismatch={entry.mismatch ? "true" : undefined}
          className={`rounded-md border px-2 py-0.5 ${entry.mismatch ? MISMATCH_CLASS : "bg-muted/50"}`}
        >
          {entry.label}
          {entry.mismatch ? " ⚠" : ""}
        </span>
      ))}
    </div>
  );
};

/**
 * Plan coverage (BLD): the status chip, which pages were read, and every gap
 * in plain words with its evidence. Shared by the floor-plan, room-map and
 * building-profile cards.
 */
export const CoverageSummary = ({
  coverage,
  testId,
}: {
  coverage: PlanCoverageView;
  testId?: string;
}) => (
  <div data-testid={testId} className="space-y-1.5 text-sm">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground">Plan coverage:</span>
      <Badge
        data-testid="coverage-status"
        variant="outline"
        className={coverage.className}
      >
        {coverage.label}
      </Badge>
      {coverage.expectedPages !== null && coverage.readPageIds.length > 0 ? (
        <span className="text-xs text-muted-foreground">
          read page{coverage.readPageIds.length === 1 ? "" : "s"}{" "}
          {coverage.readPageIds.join(", ")} of {coverage.expectedPages}
        </span>
      ) : null}
    </div>
    {coverage.gaps.length > 0 ? (
      <ul className="list-disc space-y-0.5 pl-5 text-xs">
        {coverage.gaps.map((gap) => (
          <li
            key={gap.code}
            data-testid="coverage-gap"
            data-hard={gap.hard ? "true" : undefined}
            className={
              gap.hard === false
                ? "text-muted-foreground"
                : "text-amber-700 dark:text-amber-300"
            }
          >
            {gap.label}
            {gap.hard ? " (hard gap)" : ""}
            <span className="ml-1 font-mono text-muted-foreground">
              {gap.code}
            </span>
            {gap.evidence.length > 0 ? (
              <span className="block text-muted-foreground">
                {gap.evidence.map((quote) => `"${quote}"`).join(" · ")}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    ) : null}
  </div>
);

/** BLD level details for a header row: "attic", "storey 1", "Garage". */
const levelHeaderDetails = (group: FloorPlanLevelGroup): string[] =>
  [
    humanizeLevelKind(group.kind),
    group.storey !== null ? `storey ${group.storey}` : null,
    group.building,
  ].filter((part): part is string => Boolean(part));

// One page render, signed on demand through the shared useMediaUrl path
// (POST /media/sign for `input/…` keys — the same path the Submitted Media
// grid takes). Placeholder until the URL resolves; opens the full render.
const FloorPlanPageThumb = ({
  source,
  index,
  caption,
}: {
  source: MediaUrlSource;
  index: number;
  caption: string | null;
}) => {
  const mediaUrl = useMediaUrl(source);
  const label = `Floor plan page ${index + 1}${caption ? ` — ${caption}` : ""}`;
  if (!mediaUrl.url) {
    return (
      <div
        title={label}
        className="flex h-24 w-32 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground"
      >
        {mediaUrl.isLoading
          ? "Signing…"
          : mediaUrl.status === "error"
            ? "Unavailable"
            : `page ${index + 1}`}
      </div>
    );
  }
  return (
    <a
      href={mediaUrl.url}
      target="_blank"
      rel="noreferrer"
      title={label}
      className="block overflow-hidden rounded-md border bg-white"
    >
      <img
        src={mediaUrl.url}
        alt={label}
        loading="lazy"
        className="h-24 w-auto max-w-44 object-contain"
        onError={mediaUrl.refresh}
      />
    </a>
  );
};

/** One room of the schedule — shared by the flat v1 table and the v2 groups. */
const RoomRow = ({ row }: { row: FloorPlanRoomRow }) => (
  <tr className="border-b last:border-b-0">
    <td className="px-2 py-1 font-mono text-muted-foreground">
      {row.labelRaw}
    </td>
    <td className="px-2 py-1">
      <span className="font-medium">{row.name}</span>
      {row.notes.length > 0 ? (
        <span className="ml-1 text-muted-foreground">
          ({row.notes.join(", ")})
        </span>
      ) : null}
    </td>
    <td className="px-2 py-1">{row.category}</td>
    <td className="whitespace-nowrap px-2 py-1 text-right">
      {row.area}
      {row.areaGrounded ? (
        <CheckCircle2
          className="ml-1 inline size-3 text-emerald-600"
          aria-label="grounded on plan"
        />
      ) : null}
    </td>
    <td className="px-2 py-1">{row.floor}</td>
    <td className="px-2 py-1 text-right">{row.confidence}</td>
  </tr>
);

export const FloorPlanCard = ({
  plan,
  bucket,
  propertyType = null,
}: {
  plan: EchoflicksFloorPlan;
  /** Bucket the page renders live in; null leaves the thumbnails unsigned. */
  bucket: string | null;
  /**
   * Echo-level property type (row projection or `property.type_resolved`), used
   * when the plan block itself carries none — a v1 block never does.
   */
  propertyType?: PropertyTypeSummary | null;
}) => {
  const tone = floorPlanStatusTone(plan.status);
  const summary = floorPlanSummaryLine(plan);
  const checks = countCheckEntries(plan.count_check);
  const rows = roomsTableRows(plan);
  const levelGroups = floorPlanLevelGroups(plan);
  const property = propertyTypeSummary(plan.property_type) ?? propertyType;
  const pages = floorPlanPageSources(plan, bucket);
  const pageMeta = plan.pages ?? [];
  const timings = plan.timings_ms ?? {};
  const timingLine = ["fetch", "rasterize", "read", "total"]
    .filter((key) => timings[key] !== undefined)
    .map((key) => `${key} ${formatMs(timings[key])}`)
    .join(" · ");
  const orientation = plan.orientation ?? null;
  const hasOrientation = Boolean(
    orientation &&
      (orientation.facing || orientation.note || orientation.source),
  );
  const features = plan.features ?? [];
  const candidates = plan.candidates ?? [];
  // BLD plan sets: absent on every pre-BLD block, which keeps it unchanged.
  const coverage = planCoverageView(plan.coverage);
  const coverageCandidates = coverage?.candidates ?? [];
  const sources = floorPlanSourceRows(plan);

  return (
    <div
      data-testid="floor-plan-card"
      className="space-y-3 rounded-md border p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
          <Ruler className="size-4" />
          Floor plan
        </span>
        <Badge
          data-testid="floor-plan-status"
          variant={tone.variant}
          className={tone.className}
        >
          {tone.label}
        </Badge>
        {plan.source_url ? (
          <a
            href={plan.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Open plan
            <ExternalLink className="size-3" />
          </a>
        ) : null}
        {timingLine ? (
          <span className="ml-auto text-xs text-muted-foreground">
            {timingLine}
          </span>
        ) : null}
      </div>

      {summary ? (
        <div className="text-sm text-muted-foreground">{summary}</div>
      ) : null}
      {plan.failure_reason ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          {plan.failure_reason}
        </div>
      ) : null}
      {candidates.length > 0 &&
      plan.status !== "read" &&
      coverageCandidates.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          Candidates:{" "}
          {candidates
            .map(
              (candidate) =>
                `${candidate.url_tail ?? "?"} (${candidate.source ?? "?"}${
                  typeof candidate.score === "number"
                    ? ` ${candidate.score}`
                    : ""
                })`,
            )
            .join(" · ")}
        </div>
      ) : null}

      {pages.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {pages.map((page, index) => (
            <FloorPlanPageThumb
              key={page.key}
              source={page.source}
              index={index}
              caption={floorPlanPageCaption(pageMeta[index])}
            />
          ))}
        </div>
      ) : null}

      {coverage ? (
        <div data-testid="floor-plan-coverage" className="space-y-2">
          <CoverageSummary coverage={coverage} />
          {coverageCandidates.length > 0 ? (
            <div data-testid="floor-plan-candidates" className="space-y-1">
              <div className="text-xs text-muted-foreground">
                Plan candidates ({coverageCandidates.length})
              </div>
              <ul className="space-y-0.5 text-xs">
                {coverageCandidates.map((candidate) => (
                  <li
                    key={candidate.key}
                    data-testid="floor-plan-candidate"
                    data-status={candidate.status}
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <Badge variant="outline" className={candidate.className}>
                      {candidate.status}
                    </Badge>
                    {candidate.url ? (
                      <a
                        href={candidate.url}
                        target="_blank"
                        rel="noreferrer"
                        title={candidate.url}
                        className="font-mono hover:underline"
                      >
                        {candidate.tail}
                      </a>
                    ) : (
                      <span className="font-mono">{candidate.tail}</span>
                    )}
                    {candidate.reason ? (
                      <span className="text-muted-foreground">
                        {candidate.reason}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {sources.length > 0 ? (
        <div
          data-testid="floor-plan-sources"
          className="overflow-x-auto rounded-md border"
        >
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                <th className="px-2 py-1 font-medium">#</th>
                <th className="px-2 py-1 font-medium">Plan source</th>
                <th className="px-2 py-1 font-medium">Kind</th>
                <th className="px-2 py-1 font-medium">Found by</th>
                <th className="px-2 py-1 font-medium">Status</th>
                <th className="px-2 py-1 font-medium">Pages</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr
                  key={source.key}
                  data-testid="floor-plan-source"
                  className="border-b last:border-b-0"
                >
                  <td className="px-2 py-1 text-muted-foreground">
                    {source.index ?? ""}
                  </td>
                  <td className="max-w-[16rem] truncate px-2 py-1 font-mono">
                    {source.url ? (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        title={source.url}
                        className="hover:underline"
                      >
                        {source.tail}
                      </a>
                    ) : (
                      source.tail
                    )}
                  </td>
                  <td className="px-2 py-1">{source.kind ?? ""}</td>
                  <td className="px-2 py-1">{source.foundBy.join(", ")}</td>
                  <td
                    className={`px-2 py-1 ${source.status === "dropped" ? "text-muted-foreground" : ""}`}
                  >
                    {source.status ?? ""}
                    {source.reason ? `: ${source.reason}` : ""}
                  </td>
                  <td className="px-2 py-1">{source.pages.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <CountCheckLine entries={checks} testId="floor-plan-count-check" />

      {property ? (
        <div
          data-testid="floor-plan-property-type"
          className="text-sm"
          title={
            property.evidence.length > 0
              ? `Evidence: ${property.evidence.join(" · ")}`
              : undefined
          }
        >
          <span className="text-muted-foreground">Property type: </span>
          {property.label}
          {property.source ? ` · from ${property.source}` : ""}
          {property.confidencePercent ? ` · ${property.confidencePercent}` : ""}
          {property.evidence.length > 0 ? (
            <span className="text-muted-foreground">
              {" "}
              — {property.evidence.join(" · ")}
            </span>
          ) : null}
        </div>
      ) : null}

      {hasOrientation && orientation ? (
        <div className="text-sm">
          <span className="text-muted-foreground">Orientation: </span>
          {orientation.facing
            ? `facing ${orientation.facing}`
            : "not evidenced"}
          {orientation.north_points
            ? ` · north points ${orientation.north_points}`
            : ""}
          {orientation.source
            ? ` · from ${orientation.source.replaceAll("_", " ")}`
            : ""}
          {orientation.note ? (
            <span className="text-muted-foreground"> — {orientation.note}</span>
          ) : null}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                <th className="px-2 py-1 font-medium">Label</th>
                <th className="px-2 py-1 font-medium">Name</th>
                <th className="px-2 py-1 font-medium">Category</th>
                <th className="px-2 py-1 text-right font-medium">m²</th>
                <th className="px-2 py-1 font-medium">Floor</th>
                <th className="px-2 py-1 text-right font-medium">Conf.</th>
              </tr>
            </thead>
            {levelGroups.length > 0 ? (
              // floor_plan.v2: one header row per level, rooms beneath it.
              levelGroups.map((group) => (
                <tbody key={group.key || "unassigned"}>
                  <tr
                    data-testid="floor-plan-level-header"
                    className="border-b bg-muted/30"
                  >
                    <td
                      colSpan={6}
                      className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {group.label}
                      {group.ordinal !== null ? (
                        <span className="ml-1 font-normal normal-case tracking-normal">
                          · level {group.ordinal}
                        </span>
                      ) : null}
                      {levelHeaderDetails(group).map((detail) => (
                        <span
                          key={detail}
                          data-testid="floor-plan-level-detail"
                          className="ml-1 font-normal normal-case tracking-normal"
                        >
                          · {detail}
                        </span>
                      ))}
                      <span className="ml-1 font-normal normal-case tracking-normal">
                        · {group.rows.length} room
                        {group.rows.length === 1 ? "" : "s"}
                      </span>
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <RoomRow key={row.id} row={row} />
                  ))}
                </tbody>
              ))
            ) : (
              <tbody>
                {rows.map((row) => (
                  <RoomRow key={row.id} row={row} />
                ))}
              </tbody>
            )}
          </table>
        </div>
      ) : null}

      {features.length > 0 ? (
        <div className="text-xs text-muted-foreground">
          Features:{" "}
          {features
            .map(
              (feature) =>
                `${feature.kind ?? feature.label_raw ?? "?"}${
                  typeof feature.area_m2 === "number"
                    ? ` ${formatAreaM2(feature.area_m2)}`
                    : ""
                }${
                  feature.adjacent_rooms?.length
                    ? ` (→ ${feature.adjacent_rooms.join(", ")})`
                    : ""
                }`,
            )
            .join(" · ")}
        </div>
      ) : null}
    </div>
  );
};
